import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Select, Checkbox, Row, Col, Card, Typography } from 'antd';
import { toast } from 'sonner';

const { Text } = Typography;
import { createUser } from '../../services/users';
import type { User, Account } from '../../types/user';
import { ROLES, SAMPLE_ACCOUNT_IDS } from 'src/constants/authConstants';

interface CreateUserProps {
  dpUsers: User[];
  accounts: Account[];
  onUserCreated: () => void;
  existingUsers: User[];
  hasAccess: boolean;
  currentAccountId: string;
  currentAccountName?: string;
}

const CreateUser: React.FC<CreateUserProps> = ({
  dpUsers,
  accounts,
  onUserCreated,
  existingUsers,
  hasAccess,
  currentAccountId,
  currentAccountName
}) => {
  const [form] = Form.useForm();
  const [selectedAccounts, setSelectedAccounts] = useState<Set<string>>(new Set());
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [emailValue, setEmailValue] = useState('');
  const emailInputRef = useRef<any>(null);
  const [isDev, setIsDev] = useState(false);

  useEffect(() => {
    if (emailValue && emailValue.length > 0) {
      // Get set of existing user emails for quick lookup
      const existingUserEmails = new Set(existingUsers.map((user) => user.email.toLowerCase()));

      const filtered = dpUsers.filter(
        (user) =>
          user.email.toLowerCase().includes(emailValue.toLowerCase()) &&
          user.email.toLowerCase() !== emailValue.toLowerCase() &&
          !existingUserEmails.has(user.email.toLowerCase()), // Exclude existing users
      );
      setFilteredUsers(filtered.slice(0, 5));
      setShowDropdown(filtered.length > 0);
    } else {
      setShowDropdown(false);
      setFilteredUsers([]);
    }
  }, [emailValue, dpUsers, existingUsers]);

  useEffect(() => {
    if (!hasAccess) {
      setSelectedAccounts(new Set([currentAccountId]));
    }
  }, [hasAccess, currentAccountId]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmailValue(value);
    form.setFieldValue('email', value);
  };

  const handleEmailKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Tab' &&
      emailValue.includes('@') &&
      !emailValue.includes('@modularclosets.com')
    ) {
      e.preventDefault();
      const [localPart] = emailValue.split('@');
      const newEmail = `${localPart}@modularclosets.com`;
      setEmailValue(newEmail);
      form.setFieldValue('email', newEmail);
    }
  };

  const handleSelectUser = (user: User) => {
    form.setFieldsValue({
      email: user.email,
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
    });
    setEmailValue(user.email);
    setShowDropdown(false);
  };

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  const handleAccountToggle = (accountId: string, checked: boolean) => {
    const newSelected = new Set(selectedAccounts);
    if (checked) {
      newSelected.add(accountId);
    } else {
      newSelected.delete(accountId);
    }
    setSelectedAccounts(newSelected);
  };

  const handleAddToSampleAccounts = () => {
    const newSelected = new Set(selectedAccounts);
    SAMPLE_ACCOUNT_IDS.forEach((id) => newSelected.add(id));
    setSelectedAccounts(newSelected);
  };

  const allSampleAccountsSelected = SAMPLE_ACCOUNT_IDS.every((id) => selectedAccounts.has(id));

  const handleSubmit = async (values: any) => {
    const accountIds = hasAccess
    ? Array.from(selectedAccounts)
    : [currentAccountId];

    if (hasAccess && accountIds.length === 0) {
      toast.error('Please select at least one account');
      return;
    }

    try {
      setLoading(true);

      await createUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone || '',
        role: values.role,
        accountIds,
        addToAllSampleAccounts: false,
        isDev,
      });

      form.resetFields();
      setSelectedAccounts(new Set());
      setEmailValue('');
      setIsDev(false);
      onUserCreated();
    } catch (err: any) {
      const data = err.response?.data;
      if (data?.errors) {
        const messages = Object.entries(data.errors)
          .flatMap(([field, msgs]) => (msgs as string[]).map((m) => `${field}: ${m}`))
          .join('\n');
        toast.error(data.title || 'Validation error', { description: messages });
      } else {
        toast.error('Failed to create user', {
          description: data?.message || data?.title,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={{ role: 'user' }}>
      <Row gutter={16}>
        <Col xs={24}>
          <Form.Item
            name='email'
            label='Email'
            rules={[{ required: true, message: 'Please enter email' }]}
            help='Start typing to see suggestions. Press Tab after @ to complete with modularclosets.com'
          >
            <div style={{ position: 'relative' }}>
              <Input
                ref={emailInputRef}
                placeholder='Enter email'
                onChange={handleEmailChange}
                onKeyDown={handleEmailKeyDown}
                value={emailValue}
                autoComplete='off'
                onClick={(e) => e.stopPropagation()}
              />
              {showDropdown && filteredUsers.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    zIndex: 1050,
                    backgroundColor: '#fff',
                    border: '1px solid #d9d9d9',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    marginTop: '4px',
                    maxHeight: '200px',
                    overflowY: 'auto',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {filteredUsers.map((user) => (
                    <div
                      key={user.email}
                      onClick={() => handleSelectUser(user)}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      <div style={{ color: '#000' }}>{user.email}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            name='firstName'
            label='First Name'
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder='Enter first name' />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            name='lastName'
            label='Last Name'
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder='Enter last name' />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item name='phone' label='Phone'>
            <Input placeholder='Enter phone number' />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item
            name='role'
            label='Role'
            rules={[{ required: true, message: 'Please select a role' }]}
          >
            <Select placeholder='Select role'>
              {Object.entries(ROLES).filter(([key]) => key !== 'superadmin' && key !== 'user-manager').map(([key, label]) => (
                <Select.Option key={key} value={key}>
                  {label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24}>
          <Form.Item valuePropName='checked'>
            <Checkbox checked={isDev} onChange={(e) => setIsDev(e.target.checked)}>
              Is Dev
            </Checkbox>
          </Form.Item>
        </Col>

        <Col xs={24}>
        {hasAccess ? (
          <Card size='small' title='Account Access' style={{ marginBottom: 16 }}>
            {!allSampleAccountsSelected && (
              <Button
                type='default'
                size='small'
                onClick={handleAddToSampleAccounts}
                style={{ marginBottom: 16 }}
              >
                Add to All Sample Accounts
              </Button>
            )}
            <Row gutter={[16, 8]}>
              {accounts.map((account) => (
                <Col xs={24} sm={12} key={account.accountId}>
                  <Checkbox
                    checked={selectedAccounts.has(account.accountId)}
                    onChange={(e) => handleAccountToggle(account.accountId, e.target.checked)}
                  >
                    {account.accountName} ({account.accountId})
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </Card>
        ) : (
          <Card size='small' title='Account Access' style={{ marginBottom: 16 }}>
          <Text type='secondary'>
            This user will be added to your account: <b>{currentAccountName ?? currentAccountId}</b>
          </Text>
        </Card>
        )}
        </Col>

        <Col xs={24}>
          <Form.Item>
            <Button
              type='primary'
              htmlType='submit'
              loading={loading}
              disabled={selectedAccounts.size === 0}
            >
              Create User
            </Button>
            {selectedAccounts.size === 0 && (
              <div style={{ marginTop: '8px' }}>
                <Text type='secondary'>Please select at least one account</Text>
              </div>
            )}
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CreateUser;
